import Table, { TableDetails } from "@/components/Table";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import Avatar from "@/components/Avatar";
import IconButton from "@/components/IconButton";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { useEffect, useState } from "react";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import EmptyState from "@/components/EmptyState";
import { JobPost, JobApplicationWithReviews, Review } from "../types";
import ApplicantDetailsDialog from "./ApplicantDetailsDialog";
import { useRequireCapability } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { DELETE_JOB_APPLICATION_REVIEW, JOB_POST_WITH_APPLICATION_REVIEWS_QUERY, updateCache } from "../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import ApplicantReviewsExportButton from "./ApplicantReviewsExportButton/ApplicantReviewsExportButton";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import CreateApplicationReviewDialog from "./CreateApplicationReviewDialog";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Tooltip from "@/components/Tooltip";
import Icon from "@/components/Icon";
import useCurrentUser from "@/hooks/useCurrentUser";
import { BackofficePerson } from "@/types";
import ApplicantReviewSlackRequestDialog from "./ApplicantReviewSlackRequestDialog";
import { ColumnSort } from "@tanstack/react-table";
import useCapabilities from "@/hooks/useCapabilities";
import classNames from "classnames";
import MetricGrid from "@/components/MetricGrid";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import UpdateJobApplicationFilesModal from "@/components/UpdateJobApplicationFilesModal";

const RATING_OPTIONS = [
  { value: "1", label: "Decline" },
  { value: "2", label: "Consider" },
  { value: "3", label: "Advance" }
];

const columns = (
  setMenu: (options: { rowId: string; actions: Action[] }) => void,
  onViewDetails: (application: JobApplicationWithReviews) => void,
  onAddReview: (application: JobApplicationWithReviews, isRecommendation: boolean) => void,
  onDelete: (application: JobApplicationWithReviews) => void,
  onUpdateFiles: (application: JobApplicationWithReviews) => void,
  canManageReviews: boolean
): ColumnDef<JobApplicationWithReviews>[] => [
  {
    id: "name",
    header: "Applicant Name",
    size: 400,
    accessorFn: (row) => `${row.applicant.firstName} ${row.applicant.lastName}`,
    cell: ({ row }) => (
      <div className="flex items-center py-2">
        <Avatar className="min-w-[3rem]" picture={row.original?.applicant?.profilePhoto} />
        <div className="ml-2">
          <span className="text-body-medium">
            {row.original?.applicant?.firstName} {row.original?.applicant?.lastName}
          </span>
          <br />
          <span className="text-body-small">{row.original?.applicant?.latestProgram?.programType?.name}</span>
        </div>
      </div>
    )
  },
  {
    id: "section",
    header: "Section",
    accessorKey: "section",
    accessorFn: (row) => row.applicant?.primarySection?.name || "",
    cell: ({ row }) =>
      row.original.applicant.primarySection ? (
        <div className="flex items-center py-2">{row.original.applicant.primarySection.name}</div>
      ) : null
  },
  {
    id: "resume",
    header: "Resume",
    accessorKey: "resume",
    cell: ({ row }) =>
      row.original.resume ? (
        <a
          href={row.original.resume}
          className="text-sys-brand-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noreferrer"
        >
          Resume
        </a>
      ) : null
  },
  {
    id: "coverLetter",
    header: "Cover Letter",
    accessorKey: "coverLetter",
    cell: ({ row }) =>
      row.original.coverLetter ? (
        <a
          href={row.original.coverLetter}
          className="text-label-large text-sys-brand-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noreferrer"
        >
          Cover Letter
        </a>
      ) : null
  },
  {
    id: "reviews",
    header: "Total Reviews / Average Rating",
    accessorKey: "reviewScoreAverage",
    enableSorting: true,
    sortingFn: "basic",
    cell: ({ row }) => {
      const expanded = row.getIsExpanded();
      const reviews = row.original.reviews?.filter((r) => !r.isRecommendation) || [];
      const avg = row.original.reviewScoreAverage;
      return reviews.length > 0 && avg ? (
        <div className="flex items-center py-2">
          <span>
            {reviews.length} / {avg.toFixed(2)}
          </span>
          <IconButton
            name={expanded ? "expand_less" : "expand_more"}
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            className={`ml-2 transition-transform ${expanded ? "rotate-180" : ""}`}
            aria-label={expanded ? "Collapse reviews" : "Expand reviews"}
          />
        </div>
      ) : (
        <div className="flex items-center py-2 text-body-medium opacity-20">--</div>
      );
    }
  },
  {
    id: "recommendation",
    header: "Recommendation",
    accessorKey: "recommendation",
    cell: ({ row }) => {
      const reviews = row.original.reviews || [];
      const recommendation = reviews.find((r) => r.isRecommendation);

      if (!recommendation) return <div className="flex items-center opacity-20">--</div>;

      return (
        <div className="flex items-center">
          <div className="flex items-center absolute">
            <div className="flex items-center">
              <Tooltip
                title={<div className="text-title-small text-sys-brand-on-surface-variant">Recommendation</div>}
                supportingText={
                  <div className="text-body-medium text-sys-brand-on-surface-variant">
                    <div className="mb-2">{recommendation.comments}</div>
                    <br />
                    <div>
                      <div>
                        <span className="font-semibold">Reviewer:</span> {recommendation.reviewedBy?.name || "--"}
                      </div>
                      <div>
                        <span className="font-semibold">Review Date:</span>{" "}
                        {recommendation.createdAt ? new Date(recommendation.createdAt).toLocaleDateString() : "--"}
                      </div>
                    </div>
                  </div>
                }
                menuCorner="end-start"
                anchorCorner="start-start"
              >
                <div className="flex items-center gap-2">
                  <div className="text-title-small text-sys-brand-on-surface-variant">
                    {RATING_OPTIONS.find((option) => option.value === String(recommendation.score))?.label}
                  </div>
                  <Icon name="sticky_note_2" className="text-sys-brand-on-surface" />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      );
    }
  },
  {
    id: "actions",
    header: "",
    cell: (cellProps: CellContext<JobApplicationWithReviews, unknown>) => {
      const {
        row: { id: rowId, original }
      } = cellProps;

      const hasRecommendation = original.reviews?.some((r: any) => r.isRecommendation);

      const actions: Action[] = [
        {
          text: hasRecommendation ? "Edit Recommendation" : "Add Recommendation",
          section: "Manage",
          onClick: () => onAddReview(original, true)
        },
        {
          text: "Add Review",
          section: "Manage",
          onClick: () => onAddReview(original, false)
        },
        {
          text: "Update Files",
          section: "Manage",
          onClick: () => {
            onUpdateFiles(original);
          }
        }
      ];

      if (hasRecommendation && canManageReviews) {
        actions.push({
          text: "Delete Recommendation",
          iconName: "delete",
          destructive: true,
          onClick: () => onDelete(original)
        });
      }

      if (!actions.length) return null;

      function onClick(e: React.MouseEvent) {
        e.stopPropagation();
        setMenu({ rowId, actions });
      }

      return (
        <div className="flex items-center gap-2 justify-end">
          <IconButton
            onClick={() => {
              onViewDetails(original);
            }}
            data-testid="expand-details"
            title="View Details"
            name="open_in_full"
            iconClassName="text-sys-surface-variant"
          />
          <IconButton
            id={actionId(rowId)}
            onClick={onClick}
            data-testid="actions"
            title="actions"
            name="more_vert"
            iconClassName="font-extrabold"
          />
        </div>
      );
    }
  }
];

interface ViewJobApplicantsProps {
  jobPost: JobPost;
}

export default function ViewJobPostReviews({ jobPost }: ViewJobApplicantsProps) {
  const { Menu, setMenu } = useTableRowMenu();
  const { hasPermission } = useCapabilities();
  const canManageReviews = hasPermission(PERMISSION.manageJobPosts);
  const [selectedApplicant, setSelectedApplicant] = useState<JobApplicationWithReviews | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [metrics, setMetrics] = useState<MetricCardType<Path>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [applications, setApplications] = useState<JobApplicationWithReviews[]>([]);
  const [orderBy, setOrderBy] = useState<ColumnSort[]>([{ id: "createdAt", desc: true }]);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery<{ jobPost: JobPost }>(JOB_POST_WITH_APPLICATION_REVIEWS_QUERY, {
    variables: { jobPostId: jobPost?.id },
    skip: !jobPost?.id,
    fetchPolicy: "cache-first"
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [application, setApplication] = useState<JobApplicationWithReviews | null>(null);
  const [reviewToDelete, setReviewToDelete] = useState<Review | null>(null);
  const [deleteType, setDeleteType] = useState<"review" | "recommendation" | null>(null);
  const [isRecommendation, setIsRecommendation] = useState(false);
  const [reviewToEdit, setReviewToEdit] = useState<Review | null>(null);

  // Request reviews.
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestVariables, setRequestVariables] = useState<{
    reviewers: string[];
    reviewerIds: string[];
    message: string;
  }>({
    reviewers: [],
    reviewerIds: [],
    message: ""
  });

  const { cache } = useApolloClient();
  const { user } = useCurrentUser();

  const [isRefetching, setIsRefetching] = useState(false);

  useEffect(() => {
    if (data) {
      setApplications(data.jobPost.applications || []);
      updateCache(cache, data.jobPost.id, "applications", data.jobPost.applications);
    } else {
      setApplications([]);
    }
  }, [cache, data]);

  useRequireCapability({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.manageJobPosts
  });

  const handleAddReview = (app: any, isRecommendation: boolean) => {
    const existingRecommendation = isRecommendation ? app.reviews?.find((r: any) => r.isRecommendation) : null;

    setApplication(app);
    setIsRecommendation(isRecommendation);
    setIsDialogOpen(true);

    if (existingRecommendation) {
      // Sort reviews by createdAt to get the latest one
      const sortedReviews = [...app.reviews].sort(
        (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      const latestRecommendation = sortedReviews.find((r: any) => r.isRecommendation);
      setReviewToEdit(latestRecommendation);
    } else {
      setReviewToEdit(null);
    }
  };

  useQuery(SOME_PEOPLE_QUERY, {
    variables: {
      input: {
        search: `${selectedApplicant?.applicant?.firstName} ${selectedApplicant?.applicant?.lastName}`
      },
      first: 1
    },
    skip: !selectedApplicant,
    onCompleted: (data) => {
      if (data?.people?.nodes[0]) {
        setSelectedApplicant((prev: any) => ({
          ...prev!,
          applicant: {
            ...prev!.applicant,
            ...data.people.nodes[0]
          }
        }));
      }
    }
  });

  const handleViewDetails = (application: any) => {
    setSelectedApplicant(application);
    setIsSidebarOpen(true);
  };

  const handleUpdateFiles = (application: JobApplicationWithReviews) => {
    if (!application.id) {
      showSnackbar("Application is missing", { close: true });
      return;
    }
    setSelectedApplicationId(application.id);
    setUpdateModalOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedApplicant(null);
  };

  useEffect(() => {
    setIsLoading(loading || isRefetching);
  }, [loading, isRefetching]);

  useEffect(() => {
    // Calculate total reviews
    const totalReviews = applications.reduce((total, app) => {
      return total + (app.reviews?.filter((review) => !review.isRecommendation).length || 0);
    }, 0);

    // Calculate unique reviewers
    const uniqueReviewers = new Set(
      applications.flatMap(
        (app) => app.reviews?.map((review: { reviewedBy?: { name: string } }) => review.reviewedBy?.name) || []
      )
    ).size;

    setMetrics([
      {
        label: "Total Reviews",
        value: `${totalReviews}`,
        iconName: "rate_review"
      },
      {
        label: "Unique Reviewers",
        value: `${uniqueReviewers}`,
        iconName: "rate_review"
      }
    ]);
  }, [applications]);

  const refetchApplications = () => {
    setIsRefetching(true);
    refetch().finally(() => setIsRefetching(false));
  };

  const handleDelete = (application: JobApplicationWithReviews) => {
    const recommendation = application.reviews?.find((r) => r.isRecommendation);
    if (!recommendation) return;

    setReviewToDelete(recommendation);
    setDeleteType("recommendation");
    setIsDeleteModalOpen(true);
  };

  const [deleteReview, { loading: isDeletingReview }] = useMutation(DELETE_JOB_APPLICATION_REVIEW, {
    onCompleted: () => {
      setReviewToDelete(null);
      refetchApplications();
      showSnackbar("Review deleted", { close: true });
    },
    onError: () => {
      showSnackbar("Failed to delete review", { close: true });
    }
  });

  const handleDeleteConfirm = () => {
    if (!reviewToDelete?.id) return;
    deleteReview({ variables: { id: reviewToDelete.id } });
    setIsDeleteModalOpen(false);
  };

  const handleSort = (updaterOrValue: any) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    setOrderBy(sortState);
  };

  return (
    <div className="flex flex-col gap-3">
      {!!metrics.length && <MetricGrid metrics={metrics} layout="expanded" />}

      <TableDetails
        heading="Reviews"
        body="View and manage all reviews for this job's applicants"
        ButtonComps={[
          ...(loading || isRefetching
            ? [() => <div className="animate-pulse h-6 w-42 rounded-full bg-gradient-to-r from-gray-200 to-gray-50" />]
            : [
                () => (
                  <ApplicantReviewsExportButton
                    applications={applications}
                    jobTitle={jobPost?.name}
                    facilityName={jobPost?.facility?.name}
                  />
                )
              ]),
          () => (
            <Button variant="outlined" key="request-review" icon="ios_share" onClick={() => setRequestModalOpen(true)}>
              Request Reviews
            </Button>
          )
        ]}
      />
      <Menu />
      <Table
        data={applications || []}
        columns={columns(
          setMenu,
          handleViewDetails,
          handleAddReview,
          handleDelete,
          handleUpdateFiles,
          canManageReviews
        )}
        loading={isLoading}
        onSortingChange={handleSort}
        sortingState={orderBy}
        serverSideSorting={false}
        renderFilteredEmptyState={() => (
          <EmptyState
            title="No Applicant Reviews"
            caption="There are no applicant reviews associated with this job."
            iconName="person"
          />
        )}
        getRowClasses={() => "cursor-pointer hover:bg-sys-surface-container-highest"}
        renderExpandedRow={({ row }) => {
          const regularReviews = (row.original.reviews || []).filter((review) => !review.isRecommendation);
          return (
            <div className="bg-sys-surface-container-lowest rounded-b-xl p-2">
              <TableDetailsPanel
                data={regularReviews}
                columns={[
                  {
                    accessorKey: "reviewedBy.name",
                    header: "Reviewer",
                    cell: (cellProps: CellContext<any, any>) => cellProps.row.original.reviewedBy?.name || "--"
                  },
                  {
                    accessorKey: "createdAt",
                    header: "Review Date",
                    cell: (cellProps: CellContext<any, any>) =>
                      cellProps.row.original.createdAt
                        ? new Date(cellProps.row.original.createdAt).toLocaleDateString()
                        : "--"
                  },
                  {
                    accessorKey: "score",
                    header: "Rating"
                  },
                  {
                    accessorKey: "comments",
                    header: "Review Comments"
                  },
                  {
                    id: "delete",
                    header: "",
                    cell: (cellProps: CellContext<any, any>) => {
                      const isReviewer = cellProps.row.original.reviewedBy?.name === user.name;
                      if (!isReviewer) return null;

                      return (
                        <div className="flex items-center justify-end">
                          <IconButton
                            name="delete"
                            className="text-error"
                            aria-label="Delete Review"
                            onClick={() => {
                              setReviewToDelete(cellProps.row.original);
                              setDeleteType(cellProps.row.original.isRecommendation ? "recommendation" : "review");
                            }}
                          />
                        </div>
                      );
                    }
                  }
                ]}
              />
            </div>
          );
        }}
      />
      <ApplicantDetailsDialog
        applicant={
          selectedApplicant
            ? ({
                ...selectedApplicant.applicant,
                employments: [],
                phoneNumbers: [],
                addresses: [],
                contactAddressId: null,
                membershipSince: null,
                primarySectionAffiliation: selectedApplicant.applicant.primarySection
                  ? {
                      section: selectedApplicant.applicant.primarySection
                    }
                  : undefined,
                primaryFacility: undefined
              } as unknown as BackofficePerson)
            : null
        }
        applicationEmail={selectedApplicant?.email}
        applicationPhone={selectedApplicant?.phone}
        open={isSidebarOpen}
        onClose={handleCloseSidebar}
      />
      <CreateApplicationReviewDialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setReviewToEdit(null);
        }}
        jobApplicationId={application?.id || ""}
        applicantName={`${application?.applicant.firstName} ${application?.applicant.lastName}`}
        isRecommendation={isRecommendation}
        onSuccess={refetchApplications}
        reviewToEdit={reviewToEdit}
      />
      <Dialog
        open={isDeleteModalOpen}
        onClosed={() => setIsDeleteModalOpen(false)}
        headline="Delete Recommendation"
        className="w-[490px] p-6 pr-2"
        actions={
          <div className="flex gap-2">
            <Button variant="text" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeletingReview}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              trailingIcon={false}
              icon="delete"
              onClick={handleDeleteConfirm}
              disabled={isDeletingReview}
              data-testid="confirm-delete-recommendation"
            >
              Delete
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete this recommendation? This cannot be undone.</p>
      </Dialog>
      <Dialog
        open={!!reviewToDelete}
        onClosed={() => setReviewToDelete(null)}
        headline={deleteType === "recommendation" ? "Delete Recommendation" : "Delete Review"}
        className="w-[490px] p-6 pr-2"
        actions={
          <div className="flex gap-2">
            <Button variant="text" onClick={() => setReviewToDelete(null)} disabled={isDeletingReview}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              trailingIcon={false}
              iconClassName={classNames(isDeletingReview && "animate-spin w-6 h-6 !block")}
              icon={isDeletingReview ? "progress_activity" : "delete"}
              onClick={handleDeleteConfirm}
              disabled={isDeletingReview || !reviewToDelete?.id}
              data-testid="confirm-delete-review"
            >
              Delete
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete this {deleteType}? This cannot be undone.</p>
      </Dialog>

      <ApplicantReviewSlackRequestDialog
        open={requestModalOpen}
        setOpen={setRequestModalOpen}
        requestVariables={requestVariables}
        setRequestVariables={setRequestVariables}
        disabled={false}
      />
      {updateModalOpen && selectedApplicationId && (
        <UpdateJobApplicationFilesModal
          isOpen={updateModalOpen}
          onClose={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
          }}
          applicationId={selectedApplicationId}
          onSuccess={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
            refetchApplications();
          }}
        />
      )}
    </div>
  );
}
